
  import { Vue, Component, Prop} from "vue-property-decorator";
  import { cloneDeep, isEqual } from "lodash-es";

  import GET_CUSTOM_REQUESTS from "@/apollo/requests/GetCustomRequests.gql";

  import DefaultContentLayout from "@/components/scaffold/DefaultContentLayout.vue";
  import CustomRequestModal from "@/components/requests/CustomRequestModal.vue";
  import RequestCard from "@/components/requests/RequestCard.vue";
  import CustomRequestFilters from "@/components/requests/CustomRequestFilters.vue";
  import CardPagination from "@/components/card/CardPagination.vue";
  import PlusIcon from "@/components/icons/PlusIcon.vue";
  import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
  import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
  import UIButton from "@/components/button/ui-button.vue";
  import ActionPanel from "@/components/common/ActionsPanel.vue";

  import CustomRequestsFiltersTemplate from "@/entities/filters/CustomRequestsFiltersTemplate";
  import PaginationConfig from "@/entities/PaginationConfig";
  import paginationConfig from "@/data/paginationConfig";
  import Roles from "@/entities/Roles";
  import customRequestsFiltersTemplate from "@/data/filtersTemplates/customRequestsFiltersTemplate";
  import { User, Company, Custom_Request, Custom_Request_Bool_Exp } from "@/generated/graphql";
  import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessages";
  import EmptyListImageRequests from "@/components/icons/emptyListImages/EmptyListImageRequests.vue";

  @Component({
    components: {
      EmptyListImageRequests,
      CustomRequestModal,
      PlusIcon,
      LoadingOverlay,
      EmptyListPlaceholder,
      UIButton,
      RequestCard,
      CardPagination,
      CustomRequestFilters,
      DefaultContentLayout,
      ActionPanel,
    },
    apollo: {
      requests: {
        query: GET_CUSTOM_REQUESTS,
        variables() {
          return this.filters;
        },
        update(data) {
          this.paginationConfig.loadedCount = data.requests.length;
          this.paginationConfig.total = data.requestsTotal.aggregate.count;
          return data.requests;
        }
      }
    }
  })

  export default class CustomRequests extends Vue {
    @Prop({ type: Object, required: true }) readonly currentUser!: User;
    @Prop({ type: Object, required: true }) readonly company!: Company;

    requests?: Custom_Request[] = undefined;
    paginationConfig: PaginationConfig = { ...paginationConfig };
    filtersTemplate: CustomRequestsFiltersTemplate = cloneDeep(customRequestsFiltersTemplate);
    dialog = false;

    get isDataLoaded(): boolean {
      return Boolean(this.requests?.length);
    }

    get showEmptyListPlaceholder(): boolean {
      return !Boolean(this.requests?.length);
    }

    get isAdmin(): boolean {
      return this.currentUser.role === Roles.ADMIN;
    }

    get filters(): any {
      const { page, limit } = this.paginationConfig;

      const {
        jobTitle,
        skillsIds,
        statusIds,
        managersIds
      } = this.filtersTemplate;

      const nextOffset = page * limit - limit;
      const filterConditions: Custom_Request_Bool_Exp[] = [];

      if (jobTitle) {
        filterConditions.push({
          job_title: {
            _ilike: `%${jobTitle}%`,
          }
        });
      }

      if (skillsIds.length) {
        filterConditions.push({
          custom_request_skills: {
            skill_id: {
              _in: skillsIds,
            }
          }
        });
      }

      if (managersIds.length) {
        filterConditions.push({
          created_by: {
            manager: {
              id: {
                _in: managersIds,
              }
            }
          }
        });
      }

      if (statusIds.length) {
        filterConditions.push({
          status: {
            _in: statusIds,
          }
        });
      }

      return {
        offset: nextOffset,
        limit: limit,
        where: {
          _and: filterConditions,
        }
      };
    }

    updateFilters(updatedFilters: CustomRequestsFiltersTemplate): void {
      const filtersEqual = isEqual(this.filters, updatedFilters);

      if (filtersEqual) {
        return;
      }

      this.filtersTemplate = {
        skillsIds: updatedFilters.skillsIds,
        jobTitle: updatedFilters.jobTitle,
        statusIds: updatedFilters.statusIds,
        managersIds: updatedFilters.managersIds,
      }
    }

    resetFilters(): void {
      this.filtersTemplate = cloneDeep(customRequestsFiltersTemplate);
    }

    toggleRequestModal(): void {
      this.dialog = !this.dialog;
    }

    get filtersEmpty(): boolean {
      return isEqual(this.filtersTemplate, customRequestsFiltersTemplate);
    }

    get emptyListTitle(): string {
      if (this.filtersEmpty) {
        return "No External sourcing available."
      }
      return getEmptyFiltersResultMessage("external sourcing requests");
    }


   get emptyListSubtitle(): string{
    if (this.isAdmin){
      return "You currently do not have any talent requests";
    }
    return getEmptyFiltersResultMessage("external sourcing requests");
   }

    async handleRefetch(): Promise<void> {
      this.$emit("refetchRequestsContractorsNumber");
      await this.$apollo.queries.requests.refetch();
    }




    get emptyListPlaceholder(): string {
      const entitiesName = "external sourcing requests";
  
      if (this.filtersEmpty && this.currentUser.manager) {
        return "Start adding your external sourcing requests";
      }
  
      return getEmptyFiltersResultMessage(entitiesName);
    }
  }
  